"use client";

import { SelectField } from "@/components/tool-kit/primitives";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/physics-waves/format";
import {
  type BandCount,
  COLORS,
  type ColorId,
  colorDef,
  DIGIT_COLORS,
  NO_BAND,
  type PaintedBands,
  TEMPCO_COLORS,
  TOLERANCE_COLORS,
} from "@/lib/tool-kit/physics-waves/resistor-code";

/** The strings the band pickers need; the widget's own strings object satisfies it. */
export interface DecodeInputStrings {
  colorNames: Record<ColorId, string>;
  noBandOption: string;
  bandNames: {
    digit1: string;
    digit2: string;
    digit3: string;
    multiplier: string;
    tolerance: string;
    tempco: string;
  };
  digitOption: string;
  multiplierOption: string;
  toleranceOption: string;
  tempcoOption: string;
}

const DIGIT_KEYS = ["d1", "d2", "d3"] as const;

/** One menu per band, with the colour's meaning in each option. */
export function BandPickers({
  painted,
  onChange,
  count,
  strings,
  fmt,
}: {
  painted: PaintedBands;
  onChange: (patch: Partial<PaintedBands>) => void;
  count: BandCount;
  strings: DecodeInputStrings;
  fmt: NumberFormatter;
}) {
  const name = (id: ColorId) => strings.colorNames[id];
  const factor = (exp: number) =>
    fmt(10 ** exp, { maximumFractionDigits: 2, useGrouping: false });
  const digitOptions = DIGIT_COLORS.map((id) => ({
    value: id as string,
    label: t(strings.digitOption, {
      color: name(id),
      digit: colorDef(id).digit ?? 0,
    }),
  }));
  const toleranceOptions = [
    ...TOLERANCE_COLORS.map((id) => ({
      value: id as string,
      label: t(strings.toleranceOption, {
        color: name(id),
        percent: fmt(colorDef(id).tolerance ?? 0),
      }),
    })),
    ...(count === 4
      ? [{ value: NO_BAND as string, label: strings.noBandOption }]
      : []),
  ];
  const roles = [
    strings.bandNames.digit1,
    strings.bandNames.digit2,
    strings.bandNames.digit3,
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {DIGIT_KEYS.slice(0, count === 4 ? 2 : 3).map((key, i) => (
        <SelectField
          id={`rcc-${key}`}
          key={key}
          label={roles[i]}
          onChange={(value) => onChange({ [key]: value as ColorId })}
          options={digitOptions}
          value={painted[key]}
        />
      ))}
      <SelectField
        id="rcc-mult"
        label={strings.bandNames.multiplier}
        onChange={(value) => onChange({ mult: value as ColorId })}
        options={COLORS.map((c) => ({
          value: c.id as string,
          label: t(strings.multiplierOption, {
            color: name(c.id),
            factor: factor(c.multExp),
          }),
        }))}
        value={painted.mult}
      />
      <SelectField
        id="rcc-tol"
        label={strings.bandNames.tolerance}
        onChange={(value) => onChange({ tol: value as PaintedBands["tol"] })}
        options={toleranceOptions}
        value={count === 4 || painted.tol !== NO_BAND ? painted.tol : "brown"}
      />
      {count === 6 ? (
        <SelectField
          id="rcc-tc"
          label={strings.bandNames.tempco}
          onChange={(value) => onChange({ tc: value as ColorId })}
          options={TEMPCO_COLORS.map((id) => ({
            value: id as string,
            label: t(strings.tempcoOption, {
              color: name(id),
              ppm: fmt(colorDef(id).tempco ?? 0),
            }),
          }))}
          value={painted.tc}
        />
      ) : null}
    </div>
  );
}
